import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router"
import { useState } from "react"
import { ArrowLeft, FolderOpen, Pencil, Trash2, Loader2 } from "lucide-react"
import { toast } from "sonner"
import { useDeleteRoom, useDocuments, usePatchDocument, useRooms } from "@mimir/api-client"
import type { DocumentSummary } from "@mimir/types"
import { Button } from "@/components/ui/button"
import { DocumentCard } from "@/components/mind-palace/document-card"
import { getRoomIcon } from "@/components/mind-palace/constants"
import { EmptyState } from "@/components/empty-state"
import { RoomDialog } from "@/components/mind-palace/room-dialog"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

export const Route = createFileRoute("/_app/mind-palace/room/$roomId")({
  component: RoomPage,
})

function RoomPage() {
  const { roomId } = Route.useParams()
  const navigate = useNavigate()
  const router = useRouter()

  const { data: rooms } = useRooms()
  const room = rooms?.find((r) => r.id === roomId) ?? null
  const Icon = room ? getRoomIcon(room) : FolderOpen

  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useDocuments({
    room_id: roomId,
  })
  const docs: DocumentSummary[] = data?.pages.flatMap((p) => p.data) ?? []

  const deleteRoom = useDeleteRoom()
  const patchDoc = usePatchDocument()

  const [showEditDialog, setShowEditDialog] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  const handleDelete = () => {
    if (!room) return
    deleteRoom.mutate(room.id, {
      onSuccess: () => {
        toast.success(`"${room.name}" deleted`)
        navigate({ to: "/mind-palace", search: { layout: "rooms" } })
      },
      onError: () => toast.error("Failed to delete room"),
    })
  }

  const onPinToggle = (doc: DocumentSummary) => {
    patchDoc.mutate({ id: doc.id, patch: { is_pinned: !doc.is_pinned } })
  }

  return (
    <div className="flex flex-col gap-6 h-full">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => router.history.back()}
            className="gap-2 text-muted-foreground h-8 px-2 shrink-0"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Rooms
          </Button>
          <span className="text-muted-foreground shrink-0">·</span>

          <div className="w-7 h-7 rounded-md bg-muted flex items-center justify-center shrink-0">
            <Icon className="w-3.5 h-3.5 text-muted-foreground" />
          </div>

          <h1 className="font-serif text-xl font-medium truncate">{room?.name ?? "Room"}</h1>
          <span className="text-xs text-muted-foreground shrink-0">
            {isLoading ? "…" : `${docs.length} docs`}
          </span>
        </div>

        {room && (
          <div className="flex items-center gap-1 shrink-0">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground"
              onClick={() => setShowEditDialog(true)}
              title="Edit room"
            >
              <Pencil className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={() => setShowDeleteDialog(true)}
              title="Delete room"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Description */}
      {room?.description && (
        <div className="-mt-3 shrink-0">
          <p className="text-sm text-muted-foreground max-w-2xl">{room.description}</p>
        </div>
      )}

      {/* Doc grid */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {isLoading && <p className="text-sm text-muted-foreground text-center py-10">Loading…</p>}
        {!isLoading && docs.length === 0 && (
          <EmptyState
            icon={FolderOpen}
            title="No documents"
            description="No documents in this room yet."
          />
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {docs.map((doc) => (
            <DocumentCard
              key={doc.id}
              doc={doc}
              onClick={() =>
                navigate({ to: "/mind-palace/$documentId", params: { documentId: doc.id } })
              }
              onPinToggle={() => onPinToggle(doc)}
            />
          ))}
        </div>
        {hasNextPage && (
          <div className="flex justify-center pt-4 pb-2">
            <Button
              variant="outline"
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
              className="gap-2"
            >
              {isFetchingNextPage ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
                </>
              ) : (
                "Load more"
              )}
            </Button>
          </div>
        )}
      </div>

      {room && (
        <>
          <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Delete room?</DialogTitle>
                <DialogDescription>
                  "{room.name}" will be permanently deleted. Documents inside will not be deleted.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setShowDeleteDialog(false)}
                  disabled={deleteRoom.isPending}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={deleteRoom.isPending}
                >
                  {deleteRoom.isPending ? "Deleting…" : "Delete room"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <RoomDialog room={room} open={showEditDialog} onOpenChange={setShowEditDialog} />
        </>
      )}
    </div>
  )
}
